import React, { useState, useEffect, useCallback } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { Truck, Plus, Edit2, Trash2, Search, RefreshCw, X, Save, AlertTriangle, Package, History, Phone, Mail, MapPin, CreditCard, Tag, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import { providerService, PROVIDER_TYPES, PAYMENT_MODES, PRODUCT_TYPES } from '../../services/providerService';
import { PROMO_STATUS_LABELS } from '../../services/promoSummerOrderService';

const inputCls = "w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400";

const TYPE_COLORS = {
  barrique: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  carton: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  livreur: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  fournisseur_livreur: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  fourniture: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  autre: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const defaultForm = {
  name: '',
  contactName: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  postalCode: '',
  country: 'France',
  supplierType: 'livreur',
  interventionZone: '',
  paymentMode: 'prestataire_garde',
  status: 'actif',
  internalNotes: '',
  notes: '',
};

const defaultProduct = {
  productName: '',
  productType: 'barrique_noire_270l',
  purchasePrice: '',
  clientPrice: '',
  isActive: true,
  productNotes: '',
};

export default function SuppliersManagement() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');

  // Provider form modal
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editProvider, setEditProvider] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  // Detail panel
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  // Products
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [productForm, setProductForm] = useState(defaultProduct);
  const [savingProduct, setSavingProduct] = useState(false);

  // History
  const [historyStats, setHistoryStats] = useState(null);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await providerService?.getAll();
      setProviders(data);
    } catch (e) {
      setError('Erreur lors du chargement des prestataires');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const filtered = providers?.filter(p => {
    const matchSearch =
      p?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      p?.city?.toLowerCase()?.includes(search?.toLowerCase()) ||
      p?.phone?.toLowerCase()?.includes(search?.toLowerCase());
    const matchType = typeFilter === 'all' || p?.supplierType === typeFilter;
    const matchStatus = statusFilter === 'all' || p?.status === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  // ─── Provider CRUD ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditProvider(null);
    setForm(defaultForm);
    setShowProviderModal(true);
  };

  const openEdit = (p) => {
    setEditProvider(p);
    setForm({
      name: p?.name || '',
      contactName: p?.contactName || '',
      phone: p?.phone || '',
      email: p?.email || '',
      address: p?.address || '',
      city: p?.city || '',
      postalCode: p?.postalCode || '',
      country: p?.country || 'France',
      supplierType: p?.supplierType || 'livreur',
      interventionZone: p?.interventionZone || '',
      paymentMode: p?.paymentMode || 'prestataire_garde',
      status: p?.status || 'actif',
      internalNotes: p?.internalNotes || '',
      notes: p?.notes || '',
    });
    setShowProviderModal(true);
  };

  const handleSaveProvider = async () => {
    if (!form?.name?.trim()) return;
    setSaving(true);
    try {
      if (editProvider) {
        await providerService?.update(editProvider?.id, form);
      } else {
        await providerService?.create(form);
      }
      setShowProviderModal(false);
      fetchProviders();
      if (selectedProvider?.id === editProvider?.id) {
        const updated = await providerService?.getById(editProvider?.id);
        setSelectedProvider(updated);
      }
    } catch (e) {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = async (id) => {
    if (!window.confirm('Supprimer ce prestataire ? Ses produits seront également supprimés.')) return;
    try {
      await providerService?.delete(id);
      if (selectedProvider?.id === id) setSelectedProvider(null);
      fetchProviders();
    } catch (e) {
      setError('Erreur lors de la suppression');
    }
  };

  // ─── Select provider for detail panel ──────────────────────────────────────
  const selectProvider = async (p) => {
    setSelectedProvider(p);
    setActiveTab('info');
    loadProducts(p?.id);
  };

  const loadProducts = async (providerId) => {
    setLoadingProducts(true);
    try {
      const data = await providerService?.getProductsByProvider(providerId);
      setProducts(data);
    } catch (e) {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadHistory = async (providerId) => {
    setLoadingHistory(true);
    try {
      const [stats, orders] = await Promise.all([
        providerService?.getProviderHistoryStats(providerId),
        providerService?.getProviderOrders(providerId),
      ]);
      setHistoryStats(stats);
      setHistoryOrders(orders);
    } catch (e) {
      setHistoryStats(null);
      setHistoryOrders([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'history' && selectedProvider) {
      loadHistory(selectedProvider?.id);
    }
  };

  // ─── Product CRUD ───────────────────────────────────────────────────────────
  const openAddProduct = () => {
    setEditProduct(null);
    setProductForm({ ...defaultProduct, providerId: selectedProvider?.id });
    setShowProductForm(true);
  };

  const openEditProduct = (prod) => {
    setEditProduct(prod);
    setProductForm({
      productName: prod?.productName || '',
      productType: prod?.productType || 'autre',
      purchasePrice: prod?.purchasePrice || '',
      clientPrice: prod?.clientPrice || '',
      isActive: prod?.isActive !== false,
      productNotes: prod?.productNotes || '',
    });
    setShowProductForm(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm?.productName?.trim()) return;
    setSavingProduct(true);
    try {
      if (editProduct) {
        await providerService?.updateProduct(editProduct?.id, productForm);
      } else {
        await providerService?.createProduct({ ...productForm, providerId: selectedProvider?.id });
      }
      setShowProductForm(false);
      loadProducts(selectedProvider?.id);
    } catch (e) {
      setError('Erreur lors de la sauvegarde du produit');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await providerService?.deleteProduct(id);
      loadProducts(selectedProvider?.id);
    } catch (e) {
      setError('Erreur lors de la suppression du produit');
    }
  };

  const handleProductTypeChange = (type) => {
    const typeInfo = PRODUCT_TYPES?.find(t => t?.value === type);
    setProductForm(f => ({
      ...f,
      productType: type,
      productName: typeInfo?.label || f?.productName,
    }));
  };

  const margin = Number(productForm?.clientPrice || 0) - Number(productForm?.purchasePrice || 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Truck className="h-7 w-7 text-amber-600" />
              Prestataires & Fournisseurs
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
              Référentiel complet — {providers?.length} prestataire{providers?.length !== 1 ? 's' : ''} enregistré{providers?.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchProviders} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
              <Plus className="h-4 w-4" /> Nouveau prestataire
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total', value: providers?.length, color: 'text-gray-900 dark:text-white' },
            { label: 'Actifs', value: providers?.filter(p => p?.status === 'actif')?.length, color: 'text-green-600' },
            { label: 'Livreurs', value: providers?.filter(p => p?.supplierType === 'livreur' || p?.supplierType === 'fournisseur_livreur')?.length, color: 'text-blue-600' },
            { label: 'Fournisseurs', value: providers?.filter(p => p?.supplierType === 'barrique' || p?.supplierType === 'carton')?.length, color: 'text-amber-600' },
          ]?.map(s => (
            <div key={s?.label} className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">{s?.label}</p>
              <p className={`text-2xl font-bold ${s?.color}`}>{s?.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm" />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e?.target?.value)}
            className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
            <option value="all">Tous les types</option>
            {PROVIDER_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e?.target?.value)}
            className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
            <option value="all">Tous statuts</option>
            <option value="actif">Actifs</option>
            <option value="inactif">Inactifs</option>
          </select>
        </div>

        {/* Main layout: list + detail */}
        <div className="flex gap-4">
          {/* Provider list */}
          <div className={`${selectedProvider ? 'hidden lg:block lg:w-80 shrink-0' : 'w-full'}`}>
            {loading ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">Chargement...</div>
            ) : filtered?.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Truck className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>Aucun prestataire trouvé</p>
                <button onClick={openAdd} className="mt-3 text-amber-600 hover:text-amber-700 text-sm font-medium">+ Ajouter un prestataire</button>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered?.map(p => {
                  const typeLabel = PROVIDER_TYPES?.find(t => t?.value === p?.supplierType)?.label || p?.supplierType;
                  const typeColor = TYPE_COLORS?.[p?.supplierType] || TYPE_COLORS?.autre;
                  return (
                    <div
                      key={p?.id}
                      onClick={() => selectProvider(p)}
                      className={`bg-white dark:bg-slate-800 rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${selectedProvider?.id === p?.id ? 'border-amber-500 ring-1 ring-amber-500' : 'border-gray-200 dark:border-slate-700'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{p?.name}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full shrink-0 ${p?.status === 'actif' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                              {p?.status === 'actif' ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${typeColor}`}>{typeLabel}</span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={e => { e?.stopPropagation(); openEdit(p); }} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded">
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={e => { e?.stopPropagation(); handleDeleteProvider(p?.id); }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {p?.phone && <p className="flex items-center gap-1"><Phone className="h-3 w-3" />{p?.phone}</p>}
                        {p?.city && <p className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p?.city}{p?.postalCode ? ` (${p?.postalCode})` : ''}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selectedProvider && (
            <div className="flex-1 min-w-0">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
                {/* Detail header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSelectedProvider(null)} className="lg:hidden p-1.5 text-gray-400 hover:text-gray-600 rounded">
                      <X className="h-4 w-4" />
                    </button>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selectedProvider?.name}</h2>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_COLORS?.[selectedProvider?.supplierType] || TYPE_COLORS?.autre}`}>
                        {PROVIDER_TYPES?.find(t => t?.value === selectedProvider?.supplierType)?.label}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(selectedProvider)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                      <Edit2 className="h-3.5 w-3.5" /> Modifier
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-slate-700 px-5">
                  {[
                    { id: 'info', label: 'Fiche', icon: Truck },
                    { id: 'products', label: 'Produits & Tarifs', icon: Package },
                    { id: 'history', label: 'Historique', icon: History },
                  ]?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => handleTabChange(tab?.id)}
                      className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab?.id ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab?.label}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <div className="p-5">
                  {/* INFO TAB */}
                  {activeTab === 'info' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Contact</h3>
                        {selectedProvider?.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <a href={`tel:${selectedProvider?.phone}`} className="hover:text-amber-600">{selectedProvider?.phone}</a>
                          </div>
                        )}
                        {selectedProvider?.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <a href={`mailto:${selectedProvider?.email}`} className="hover:text-amber-600">{selectedProvider?.email}</a>
                          </div>
                        )}
                        {(selectedProvider?.address || selectedProvider?.city) && (
                          <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                            <span>
                              {selectedProvider?.address && <span>{selectedProvider?.address}<br /></span>}
                              {selectedProvider?.postalCode} {selectedProvider?.city}
                              {selectedProvider?.country && selectedProvider?.country !== 'France' && `, ${selectedProvider?.country}`}
                            </span>
                          </div>
                        )}
                        {selectedProvider?.interventionZone && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Tag className="h-4 w-4 text-gray-400" />
                            <span>Zone : {selectedProvider?.interventionZone}</span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Conditions</h3>
                        <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <CreditCard className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                          <span>{PAYMENT_MODES?.find(m => m?.value === selectedProvider?.paymentMode)?.label || selectedProvider?.paymentMode}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          {selectedProvider?.status === 'actif'
                            ? <CheckCircle className="h-4 w-4 text-green-500" />
                            : <XCircle className="h-4 w-4 text-gray-400" />}
                          <span className={selectedProvider?.status === 'actif' ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>
                            {selectedProvider?.status === 'actif' ? 'Prestataire actif' : 'Prestataire inactif'}
                          </span>
                        </div>
                        {selectedProvider?.internalNotes && (
                          <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                            <p className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-1">Notes internes</p>
                            <p className="text-sm text-yellow-800 dark:text-yellow-300">{selectedProvider?.internalNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* PRODUCTS TAB */}
                  {activeTab === 'products' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Produits & Tarifs ({products?.length})
                        </h3>
                        <button onClick={openAddProduct} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
                          <Plus className="h-4 w-4" /> Ajouter un produit
                        </button>
                      </div>

                      {loadingProducts ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">Chargement...</div>
                      ) : products?.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">Aucun produit enregistré</p>
                          <button onClick={openAddProduct} className="mt-2 text-amber-600 hover:text-amber-700 text-sm font-medium">+ Ajouter un produit</button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {/* Header */}
                          <div className="hidden sm:grid grid-cols-5 gap-3 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <span className="col-span-2">Produit</span>
                            <span className="text-right">Prix achat</span>
                            <span className="text-right">Prix client</span>
                            <span className="text-right">Marge ADT</span>
                          </div>
                          {products?.map(prod => (
                            <div key={prod?.id} className={`rounded-xl border p-3 ${prod?.isActive ? 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600' : 'bg-gray-50 dark:bg-slate-800 border-gray-100 dark:border-slate-700 opacity-60'}`}>
                              <div className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm text-gray-900 dark:text-white">{prod?.productName}</span>
                                    {!prod?.isActive && <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded">Inactif</span>}
                                  </div>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {PRODUCT_TYPES?.find(t => t?.value === prod?.productType)?.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm shrink-0">
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Achat</p>
                                    <p className="font-semibold text-orange-600">{prod?.purchasePrice} €</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Client</p>
                                    <p className="font-semibold text-blue-600">{prod?.clientPrice} €</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Marge</p>
                                    <p className={`font-bold ${prod?.adtMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{prod?.adtMargin} €</p>
                                  </div>
                                  <div className="flex gap-1">
                                    <button onClick={() => openEditProduct(prod)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded">
                                      <Edit2 className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => handleDeleteProduct(prod?.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {prod?.productNotes && (
                                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 italic">{prod?.productNotes}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Product form inline */}
                      {showProductForm && (
                        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                          <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-3">
                            {editProduct ? 'Modifier le produit' : 'Nouveau produit'}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Type de produit</label>
                              <select value={productForm?.productType} onChange={e => handleProductTypeChange(e?.target?.value)} className={inputCls}>
                                {PRODUCT_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nom du produit *</label>
                              <input type="text" value={productForm?.productName} onChange={e => setProductForm(f => ({ ...f, productName: e?.target?.value }))} className={inputCls} placeholder="Ex: Barrique noire 270 L" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Prix achat / coût prestataire (€)</label>
                              <input type="number" min="0" step="0.01" value={productForm?.purchasePrice} onChange={e => setProductForm(f => ({ ...f, purchasePrice: e?.target?.value }))} className={inputCls} placeholder="30" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Prix de vente client (€)</label>
                              <input type="number" min="0" step="0.01" value={productForm?.clientPrice} onChange={e => setProductForm(f => ({ ...f, clientPrice: e?.target?.value }))} className={inputCls} placeholder="50" />
                            </div>
                            {(productForm?.purchasePrice || productForm?.clientPrice) && (
                              <div className="sm:col-span-2">
                                <div className={`p-2 rounded-lg text-sm font-medium ${margin >= 0 ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                                  <TrendingUp className="inline h-4 w-4 mr-1" />
                                  Marge ADT calculée : <strong>{margin?.toFixed(2)} €</strong>
                                </div>
                              </div>
                            )}
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes produit</label>
                              <input type="text" value={productForm?.productNotes} onChange={e => setProductForm(f => ({ ...f, productNotes: e?.target?.value }))} className={inputCls} placeholder="Informations complémentaires..." />
                            </div>
                            <div className="sm:col-span-2 flex items-center gap-2">
                              <input type="checkbox" id="prodActive" checked={productForm?.isActive} onChange={e => setProductForm(f => ({ ...f, isActive: e?.target?.checked }))} className="rounded" />
                              <label htmlFor="prodActive" className="text-sm text-gray-700 dark:text-gray-300">Produit actif</label>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button onClick={() => setShowProductForm(false)} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300">Annuler</button>
                            <button onClick={handleSaveProduct} disabled={savingProduct || !productForm?.productName?.trim()} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
                              <Save className="h-3.5 w-3.5" /> {savingProduct ? 'Sauvegarde...' : 'Sauvegarder'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* HISTORY TAB */}
                  {activeTab === 'history' && (
                    <div>
                      {loadingHistory ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">Chargement de l'historique...</div>
                      ) : (
                        <>
                          {/* KPIs */}
                          {historyStats && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                              {[
                                { label: 'Commandes', value: historyStats?.total_orders, color: 'text-gray-900 dark:text-white', suffix: '' },
                                { label: 'Barriques', value: historyStats?.total_barrels, color: 'text-blue-600', suffix: '' },
                                { label: 'Coût prestataire', value: `${Number(historyStats?.total_provider_cost || 0)?.toFixed(0)} €`, color: 'text-orange-600', suffix: '' },
                                { label: 'Reversé ADT', value: `${Number(historyStats?.total_adt_revenue || 0)?.toFixed(0)} €`, color: 'text-green-600', suffix: '' },
                              ]?.map(s => (
                                <div key={s?.label} className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 border border-gray-200 dark:border-slate-600">
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{s?.label}</p>
                                  <p className={`text-xl font-bold ${s?.color}`}>{s?.value}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Orders list */}
                          {historyOrders?.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                              <History className="h-8 w-8 mx-auto mb-2 opacity-30" />
                              <p className="text-sm">Aucune commande liée à ce prestataire</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Commandes promo ({historyOrders?.length})</h4>
                              {historyOrders?.map(order => (
                                <div key={order?.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 text-sm">
                                  <div>
                                    <p className="font-medium text-gray-900 dark:text-white">{order?.client_name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {order?.barrel_quantity} barrique{order?.barrel_quantity > 1 ? 's' : ''} · {new Date(order?.created_at)?.toLocaleDateString('fr-FR')}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-gray-900 dark:text-white">{(order?.barrel_quantity * (order?.unit_provider_cost || 30))?.toFixed(0)} €</p>
                                    <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                                      {PROMO_STATUS_LABELS?.[order?.order_status] || order?.order_status}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Provider form modal */}
      {showProviderModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editProvider ? 'Modifier le prestataire' : 'Nouveau prestataire'}
              </h2>
              <button onClick={() => setShowProviderModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de prestataire *</label>
                <select value={form?.supplierType} onChange={e => setForm(f => ({ ...f, supplierType: e?.target?.value }))} className={inputCls}>
                  {PROVIDER_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
                </select>
              </div>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom *</label>
                <input type="text" value={form?.name} onChange={e => setForm(f => ({ ...f, name: e?.target?.value }))} className={inputCls} placeholder="Ex: Thierno" />
              </div>
              {/* Phone + Email */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                  <input type="tel" value={form?.phone} onChange={e => setForm(f => ({ ...f, phone: e?.target?.value }))} className={inputCls} placeholder="07 XX XX XX XX" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input type="email" value={form?.email} onChange={e => setForm(f => ({ ...f, email: e?.target?.value }))} className={inputCls} />
                </div>
              </div>
              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Adresse</label>
                <input type="text" value={form?.address} onChange={e => setForm(f => ({ ...f, address: e?.target?.value }))} className={inputCls} placeholder="12 rue de la Paix" />
              </div>
              {/* City + Postal */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ville</label>
                  <input type="text" value={form?.city} onChange={e => setForm(f => ({ ...f, city: e?.target?.value }))} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code postal</label>
                  <input type="text" value={form?.postalCode} onChange={e => setForm(f => ({ ...f, postalCode: e?.target?.value }))} className={inputCls} />
                </div>
              </div>
              {/* Zone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zone d'intervention</label>
                <input type="text" value={form?.interventionZone} onChange={e => setForm(f => ({ ...f, interventionZone: e?.target?.value }))} className={inputCls} placeholder="Ex: Île-de-France, Paris 75..." />
              </div>
              {/* Payment mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mode de paiement</label>
                <select value={form?.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e?.target?.value }))} className={inputCls}>
                  {PAYMENT_MODES?.map(m => <option key={m?.value} value={m?.value}>{m?.label}</option>)}
                </select>
              </div>
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Statut</label>
                <select value={form?.status} onChange={e => setForm(f => ({ ...f, status: e?.target?.value }))} className={inputCls}>
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                </select>
              </div>
              {/* Internal notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes internes</label>
                <textarea value={form?.internalNotes} onChange={e => setForm(f => ({ ...f, internalNotes: e?.target?.value }))} rows={2} className={inputCls} placeholder="Notes visibles uniquement par l'équipe ADT..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-slate-700 shrink-0">
              <button onClick={() => setShowProviderModal(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300">Annuler</button>
              <button onClick={handleSaveProvider} disabled={saving || !form?.name?.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
